import { ReactElement } from 'react'
import { TabContext, TabPanel, TabList } from '@mui/lab'
import { Box, styled, Tab } from '@mui/material'
import { useElementTypes, useHideElements } from '@editor/hooks'
import { useSelectionStore } from '@editorStores'
import { RoofForm, SlabForm } from '../RoofDrawing'
import { ElementFormMode } from '../elementFormMode'
import HorizontalDistributionForm from './HorizontalDistributionForm'

const StyledTabPanel = styled(TabPanel)({
  padding: '8px',
})

interface Props {
  onClose: () => void
  selectedElements: string[]
  elementType: ElementTypes
  selectElement: (element: string | null) => void
  /**
   * Hide the tab headers and show only the "Bauteil(e)" tab content. Used where the force
   * distribution tab is not relevant (e.g. the IFC import planar-model editing drawer).
   */
  hideTabs?: boolean
  /**
   * `architectural` hides the problems / warnings section — used by the IFC importer. Defaults to
   * `all`, which shows every section.
   */
  mode?: ElementFormMode
}

const RoofSlabForm = ({ hideTabs = false, mode = 'all', ...props }: Props): ReactElement | null => {
  // The form tab lives in the selection store's activeTab slot (like the wall form tabs), so it
  // is scoped to the current selection instead of lingering in the URL.
  const activeTab = useSelectionStore(state => state.activeTab)
  const setActiveTab = useSelectionStore(state => state.setActiveTab)

  // The tab slot is shared with the other forms (e.g. the wall form's named tabs), so a stale
  // value that isn't one of this form's keys falls back to the first tab.
  const tab = !hideTabs && activeTab === '1' ? '1' : '0'

  useHideElements(tab === '0' ? (props.selectedElements[0] as string) : null)

  const elementTypes = useElementTypes()

  return (
    <Box>
      <TabContext value={tab}>
        {!hideTabs && (
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                fontSize: 12,
                paddingX: ({ spacing }) => spacing(1.5),
              },
            }}
          >
            <TabList onChange={(_, value) => setActiveTab(value)}>
              <Tab value="0" label="Bauteil(e)" />
              <Tab value="1" label="Kräfteverteilung" data-cy="tab-forces" />
            </TabList>
          </Box>
        )}
        <StyledTabPanel value="0">
          {elementTypes[props.selectedElements[0]] === 'roof_slabs' ? (
            <RoofForm
              selectedElement={props.selectedElements[0]}
              selectElement={props.selectElement}
              mode={mode}
            />
          ) : (
            <SlabForm
              selectedElement={props.selectedElements[0]}
              selectElement={props.selectElement}
              elementType="slabs"
              mode={mode}
            />
          )}
        </StyledTabPanel>
        <StyledTabPanel value="1">
          <HorizontalDistributionForm {...props} />
        </StyledTabPanel>
      </TabContext>
    </Box>
  )
}

export default RoofSlabForm
